/**
 * Calendar dates — the ones with no time in them.
 *
 * A `date` column comes back as 'YYYY-MM-DD'. `new Date('2026-10-31')` parses
 * that as midnight **UTC**, so anywhere west of Greenwich `toLocaleDateString()`
 * renders the day before: a check-by date entered as 31 October reads "Oct 30".
 * Catchball due dates and commitment review dates were both doing this.
 *
 * A calendar date has no instant and no timezone — it is the day someone wrote
 * down. So these parse the parts and build the date in local terms, which makes
 * the rendered day equal the stored day everywhere on earth.
 *
 * Use these for date-only values. For a real timestamp (created_at, decided_at)
 * keep using the Date the server sent — those DO have an instant, and shifting
 * them to local time is correct.
 */

const CALENDAR_DATE = /^(\d{4})-(\d{2})-(\d{2})/;

/**
 * A 'YYYY-MM-DD' value as a Date at local midnight. Returns null when the value
 * is empty or is not a calendar date, so callers can decide what to show.
 */
export function parseCalendarDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const m = CALENDAR_DATE.exec(value);
  if (!m) return null;
  const [, y, mo, d] = m;
  const date = new Date(Number(y), Number(mo) - 1, Number(d));
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * A calendar date rendered in the reader's locale, on the day it actually says.
 * Falls back to the raw value when it cannot be parsed, which is more useful to
 * a person than "Invalid Date".
 */
export function formatCalendarDate(
  value: string | null | undefined,
  options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' },
): string {
  const date = parseCalendarDate(value);
  if (!date) return value ?? '';
  return date.toLocaleDateString(undefined, options);
}

/**
 * A moment for a person to read: "Oct 13, 2:05 PM", or with the year when the
 * moment may be far from today. Empty and invalid values are '' — never
 * "Invalid Date". One copy; the catchball thread composer re-exports it.
 */
export function formatWhen(iso: string | null | undefined, options: { withYear?: boolean } = {}): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    ...(options.withYear ? { year: 'numeric' } : {}),
    hour: 'numeric',
    minute: '2-digit',
  });
}
